import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'; // Modulo dee Angular http
import { Router } from '@angular/router';
import { Especialidad } from '../models/Especialidad';
import { catchError, Observable, of, throwError } from 'rxjs';
import { OrdenPrestador } from '../models/OrdenPrestador';
import { Prestador } from '../models/Prestador';
import { Prestacion } from '../models/Prestacion';
import { Ubicacion } from '../models/Ubicacion';
import { API_URI } from 'src/environments/environment';
import { CalificacionOrdenPrestador } from '../models/CalificacionOrdenPrestador';
import { EstadoOrden } from '../models/EstadoOrden';
import { UsuarioService } from './usuario.service';

// Fila de la lista admin ("Órdenes de Prestador" en More) — ver
// ordenPrestadorController.buscarAdmin en el backend.
export interface OrdenPrestadorAdminRow {
  id: number;
  id_cliente: string;
  id_prestador: string;
  id_estado_orden: number;
  d_fecha_turno: string | Date | null;
  d_date_create: string | Date | null;
  n_precio_turno?: number | null;
  estadoOrden: string;
  nombreCliente: string;
  nombrePrestador: string;
  nombreEspecialidad?: string | null;
  nombrePrestacion?: string | null;
}

export interface OrdenPrestadorAdminResponse {
  rows: OrdenPrestadorAdminRow[];
  total: number;
  page: number;
  limit: number;
}

@Injectable({
  providedIn: 'root'
})
export class OrderPrestadorService {
  private readonly _usuarioService = inject(UsuarioService);
  private readonly API_URI: string = API_URI + 'app';
  especialidades: Especialidad[] = [];
  prestadores: Prestador[] = [];
  prestaciones: Prestacion[] = [];
  ordenesPrestador: OrdenPrestador[] = [];

  ordenPrestador: OrdenPrestador = {
    id: null,
    id_solicitud: null,
    id_cliente: null,
    id_prestador: null,
    id_especialidad: null,
    id_prestacion: null,
    n_latitud: null,
    n_longitud: null,
    x_zona: null,
    b_manana: false,
    b_tarde: false,
    b_noche: false,
    d_fecha_uno: null,
    d_fecha_dos: null,
    d_fecha_tres: null,
    d_fecha_uno_prestador: null,
    d_fecha_dos_prestador: null,
    d_fecha_tres_prestador: null,
    d_fecha_turno: null,
    id_ubicacion: null,
  };

  // Ubicaciones activas del prestador elegido directamente (flujo "turno con prestador puntual")
  // y la que el cliente terminó eligiendo (o la única, si no hubo que elegir).
  ubicacionesPrestador: Ubicacion[] = [];
  ubicacionSeleccionada?: Ubicacion;

  img_receta?: File;
  preview_img_receta?: string;

  constructor(private http: HttpClient, private router: Router) {
    this.llenarDatos();
  }

  private llenarDatos(){
    this._usuarioService.getEspecialidades(true).subscribe({
      next: (res) =>  {
        this.especialidades = res.map(es => ({
          ...es,
          b_activo: !!es.b_activo // convierte 1 → true y 0 → false
        }));
      },
      error: (e) => console.log("Error al obtener las Especialidades"),
      complete: () => console.log("Especialidades obtenidas"),
    });

    this._usuarioService.getPrestaciones(true).subscribe({
      next: (res) =>  {
        this.prestaciones = res.map(pr => ({
          ...pr,
          b_activo: !!pr.b_activo // convierte 1 → true y 0 → false
        }));
      },
      error: (e) => console.log("Error al obtener las Prestaciones"),
      complete: () => console.log("Prestaciones obtenidas"),
    });
  }

  getMisPrestadoresXUsuario(id_usuario: string): Observable<Prestador[]>{
    return this.http.get<Prestador[]>(`${this.API_URI}/prestadores/xUsuario/${id_usuario}`);
  }

  getPrestadoresDefault(b_activo: boolean){
    this._usuarioService.getPrestadores(b_activo).subscribe({
      next: (res) =>  {
        this.prestadores = res.map(pr => ({
          ...pr,
          b_reprogramacion_turno: !!pr.b_reprogramacion_turno // convierte 1 → true y 0 → false
        }));
      },
      error: (e) => console.log("Error al obtener los Prestadores"),
      complete: () => console.log("Prestadores obtenidos"),
    });
  }

  getOrdenPrestador(id: number): Observable<OrdenPrestador | null>{
    return this.http
      .get<OrdenPrestador>(`${this.API_URI}/ordenesPrestador/xId/${id}`)
      .pipe(
        catchError((err) => {
          if (err?.status === 404) return of(null);   // no existe calificación => ok
          return throwError(() => err);               // cualquier otro error => que explote
        })
      );
  }

  getOrdenesPrestadorXUsuario(id_usuario: string): Observable<OrdenPrestador[]>{
    return this.http.get<OrdenPrestador[]>(`${this.API_URI}/ordenesPrestador/xUsuario/${id_usuario}`);
  }

  getOrdenesPrestadorXPrestador(id_prestador: string): Observable<OrdenPrestador[]>{
    return this.http.get<OrdenPrestador[]>(`${this.API_URI}/ordenesPrestador/xPrestador/${id_prestador}`);
  }

  getOrdenesPrestadorXSolicitud(id_solicitud: string): Observable<OrdenPrestador[]>{
    return this.http.get<OrdenPrestador[]>(`${this.API_URI}/ordenesPrestador/xSolicitud/${id_solicitud}`);
  }

  getEstadosOrdenes(b_activo?: boolean): Observable<EstadoOrden[]>{
    const url = b_activo === undefined
      ? `${this.API_URI}/estadosOrdenes/buscar`
      : `${this.API_URI}/estadosOrdenes/buscar/${Number(b_activo)}`;

    return this.http.get<EstadoOrden[]>(url);
  }

  // Listado paginado y buscable para el admin (pantalla "Órdenes de Prestador" en More).
  buscarAdmin(params: { page: number; limit: number; search?: string; id_estado_orden?: number | null }): Observable<OrdenPrestadorAdminResponse> {
    let httpParams = new HttpParams().set('page', params.page).set('limit', params.limit);
    if (params.search) httpParams = httpParams.set('search', params.search);
    if (params.id_estado_orden) httpParams = httpParams.set('id_estado_orden', params.id_estado_orden);
    return this.http.get<OrdenPrestadorAdminResponse>(`${this.API_URI}/ordenesPrestador/admin`, { params: httpParams });
  }

  getCalificacionesOrdenPrestadorXUsuario(id_usuario: string): Observable<CalificacionOrdenPrestador[]>{
    return this.http.get<CalificacionOrdenPrestador[]>(`${this.API_URI}/calificacionesOrdenesPrestador/xUsuario/${id_usuario}`);
  }

  getCalificacionesOrdenPrestadorXPrestador(id_prestador: string): Observable<CalificacionOrdenPrestador[]>{
    return this.http.get<CalificacionOrdenPrestador[]>(`${this.API_URI}/calificacionesOrdenesPrestador/xPrestador/${id_prestador}`);
  }

  getCalificacionOrdenPrestadorXOrdenPrestador(id_orden_prestador: number): Observable<CalificacionOrdenPrestador | null>{
    return this.http
      .get<CalificacionOrdenPrestador>(`${this.API_URI}/calificacionesOrdenesPrestador/xOrdenPrestador/${id_orden_prestador}`)
      .pipe(
        catchError((err) => {
          if (err?.status === 404) return of(null);   // no existe calificación => ok
          return throwError(() => err);               // cualquier otro error => que explote
        })
      );
  }

  private createOrdenPrestador(): Observable<any>{
    delete this.ordenPrestador.d_date_create;
    delete this.ordenPrestador.d_date_updated;
    delete this.ordenPrestador.estadoOrden;
    delete this.ordenPrestador.imgAvatarCliente;
    delete this.ordenPrestador.imgAvatarPrestador;
    delete this.ordenPrestador.nombreCliente;
    delete this.ordenPrestador.nombrePrestador;
    delete this.ordenPrestador.nombreEspecialidad;
    delete this.ordenPrestador.nombrePrestacion;
    this.ordenPrestador.id_estado_orden = 1; // INICIADO

    const data = {
      ordenPrestador: this.ordenPrestador
    };
    return this.http.post<any>(`${this.API_URI}/ordenesPrestador`, data);
  }

  private updateOrdenPrestadorImgReceta(x_img_receta: File, id_solicitud: string): Observable<any> {
    const formularioDeDatos = new FormData();
    formularioDeDatos.append('img_receta', x_img_receta);
    return this.http.post(`${this.API_URI}/ordenesPrestador/receta/xSolicitud/${id_solicitud}`, formularioDeDatos);
  }

  createCalificacionOrdenPrestador(calificacionOrdenPrestador: CalificacionOrdenPrestador): Observable<any>{
    return this.http.post<any>(`${this.API_URI}/calificacionesOrdenesPrestador`, calificacionOrdenPrestador);
  }

  updateOrdenPrestador(ordenPrestador: OrdenPrestador): Observable<void>{
    delete ordenPrestador.d_date_create;
    delete ordenPrestador.d_date_updated;
    delete ordenPrestador.estadoOrden;
    delete ordenPrestador.imgAvatarCliente;
    delete ordenPrestador.imgAvatarPrestador;
    delete ordenPrestador.nombreCliente;
    delete ordenPrestador.nombrePrestador;
    delete ordenPrestador.nombreEspecialidad;
    delete ordenPrestador.nombrePrestacion;

    const data = {
      ordenPrestador: ordenPrestador
    };
    return this.http.put<void>(`${this.API_URI}/ordenesPrestador`, data);
  }

  updateEstadoOrdenPrestador(id_orden_prestador: number, id_estado_orden: number): Observable<void>{
    let estadoOrdenPrestador = {
      id_orden_prestador: id_orden_prestador,
      id_estado_orden: id_estado_orden
    }
    return this.http.put<void>(`${this.API_URI}/ordenesPrestador/estadoOrdenPrestador`, estadoOrdenPrestador);
  }

  finalizeOrdenPrestador(): Promise<any> {
    return new Promise((resolved, reject) => {
      this.createOrdenPrestador().subscribe({
        next: (resCoP) => {
          if (!this.img_receta || !resCoP?.id_solicitud) {
            this.clearData();
            resolved(resCoP);
            return;
          }

          this.updateOrdenPrestadorImgReceta(this.img_receta, resCoP.id_solicitud).subscribe({
            next: () => {
              this.clearData();
              resolved(resCoP);
            },
            error: (eIR) => {
              console.error(eIR);
              // La solicitud ya fue creada en el backend; no limpio data para no perder la receta en un reintento
              reject({ message: 'La solicitud se envió, pero no se pudo adjuntar la receta.' });
            },
          });
        },
        error: (eCoP) => {
          reject(eCoP.error.error);
          console.error(eCoP);
        },
        complete: () => console.log("Create Orden de Prestador completada"),
      });
    });
  }

  responseOrdenPrestador(ordenPrestador: OrdenPrestador): Promise<any> {
    return new Promise((resolved, reject) => {
      ordenPrestador.id_estado_orden = 2;
      this.updateOrdenPrestador(ordenPrestador).subscribe({
        next: (resUoP) => {
          this.clearData();
          resolved(resUoP);
        },
        error: (eUoC) => {
          reject('Error al actualizar la Orden de Prestador');
          console.error(eUoC);
        },
        complete: () => console.log("Update Orden de Prestador completada"),
      });
    });
  }

  pagarMercadoPago(ordenPrestador: OrdenPrestador): Observable<any>{
    const data = {
      ordenPrestador: ordenPrestador
    };
    return this.http.put<any>(`${this.API_URI}/mercadoPago/createPreferencesPrestador`, data);
  }

  clearData(){
    const ordenPrestador: OrdenPrestador = {
      id: null,
      id_solicitud: null,
      id_cliente: null,
      id_prestador: null,
      id_especialidad: null,
      id_prestacion: null,
      n_latitud: null,
      n_longitud: null,
      x_zona: null,
      b_manana: false,
      b_tarde: false,
      b_noche: false,
      d_fecha_uno: null,
      d_fecha_dos: null,
      d_fecha_tres: null,
      d_fecha_uno_prestador: null,
      d_fecha_dos_prestador: null,
      d_fecha_tres_prestador: null,
      d_fecha_turno: null,
      id_ubicacion: null,
    };

    this.ordenPrestador = ordenPrestador;
    this.ordenesPrestador = [];
    this.ubicacionesPrestador = [];
    this.ubicacionSeleccionada = undefined;
    this.img_receta = undefined;
    this.preview_img_receta = undefined;
  }
}
